/**
 * In-memory cache of finding source documents, warmed in the background so
 * opening a finding renders its source instantly with NO network request.
 *
 * Why not just rely on the browser's HTTP cache + <link rel=prefetch>? Because
 * that is unreliable in practice: rel=prefetch runs at the browser's lowest
 * priority (often starved on a busy page) and Safari barely supports it, and a
 * cross-origin prefetched response is not guaranteed to be reused by a later
 * iframe. So we take control: fetch the actual document bytes into JS memory
 * and render from memory (EUR-Lex HTML via <iframe srcDoc>, so there is no
 * request at all on open). This is browser-independent and cannot be starved.
 *
 * EUR-Lex articles (the dominant, and the reported-slow, case) are cached as
 * HTML strings — small, and rendered via srcDoc for a true zero-network open.
 * PDFs stream as bytes and are heavy to hold in memory, so they are not stored
 * here; instead their (now immutable-cacheable) endpoint is warmed via an
 * eager fetch so the browser HTTP cache serves the re-open. The finding view
 * keeps its network <iframe src> for PDFs.
 */

export type SourceDocumentEntry =
  | { status: 'loading' }
  | { status: 'ready'; html: string };

const cache = new Map<string, SourceDocumentEntry>();
const warmedPdfUrls = new Set<string>();
const listeners = new Set<() => void>();

// Bounded concurrency: each warm is a cross-origin GET against the
// (cold-startable) orchestrator. rel=prefetch used to lean on the browser to
// throttle; now we throttle ourselves so a big workspace doesn't burst.
const MAX_CONCURRENCY = 4;
let inFlight = 0;
const queue: string[] = [];

// The cache is module-global and lives for the session; bound it so a long
// run across many workspaces can't grow the heap without limit. Comfortably
// exceeds any single workspace's distinct-document count; oldest ready entries
// are evicted first. Evicting a viewed entry is safe — its HTML was already
// copied into the open iframe's srcDoc (a detached string, no blob to revoke).
const MAX_CACHE_ENTRIES = 150;

const isPdfUrl = (url: string): boolean => url.includes('/pdf/');

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeSourceDocuments(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The cached HTML entry for a URL, or undefined if never warmed. */
export function getSourceDocument(
  url: string | null | undefined,
): SourceDocumentEntry | undefined {
  return url ? cache.get(url) : undefined;
}

/**
 * Warm a finding's source document. EUR-Lex HTML is fetched into memory;
 * PDFs get a best-effort HTTP-cache warm. Idempotent — a URL already warmed
 * (or in flight) is skipped.
 */
export function warmSourceDocument(url: string): void {
  if (!url) return;

  if (isPdfUrl(url)) {
    if (warmedPdfUrls.has(url)) return;
    warmedPdfUrls.add(url);
    // Populate the browser HTTP cache (endpoint is immutable-cacheable); the
    // PDF iframe's src then re-opens from cache. Fire-and-forget, opaque.
    void fetch(url, { mode: 'no-cors' }).catch(() => {
      warmedPdfUrls.delete(url); // let a later attempt retry
    });
    return;
  }

  if (cache.has(url)) return; // loading | ready | error already
  cache.set(url, { status: 'loading' });
  notify();
  queue.push(url);
  pump();
}

function onLoadSettled(): void {
  inFlight -= 1;
  pump();
}

function pump(): void {
  while (inFlight < MAX_CONCURRENCY && queue.length > 0) {
    const url = queue.shift() as string;
    inFlight += 1;
    void loadHtml(url).finally(onLoadSettled);
  }
}

function storeReady(url: string, html: string): void {
  cache.set(url, { status: 'ready', html });
  // Evict oldest entries beyond the cap (Map preserves insertion order).
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

async function loadHtml(url: string): Promise<void> {
  try {
    const res = await fetch(url, { credentials: 'omit' });
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    if (!res.ok || contentType.includes('application/pdf')) {
      // Don't cache a terminal error — that would permanently disable the
      // instant path for this doc even after a transient failure. Drop the
      // entry so a later warm/open retries; the network iframe covers the gap.
      cache.delete(url);
    } else {
      storeReady(url, await res.text());
    }
  } catch {
    cache.delete(url); // transient failure — retryable
  }
  notify();
}

/** Test-only: reset module state between cases. */
export function __resetSourceDocumentCache(): void {
  cache.clear();
  warmedPdfUrls.clear();
  listeners.clear();
  queue.length = 0;
  inFlight = 0;
}
